import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { logError } from "../lib/logger";
import { useAuth } from "../context/AuthContext";

const MAX_ATTEMPTS = 20;

export function usePaymentPolling() {
  const [params, setParams] = useSearchParams();
  const [paymentState, setPaymentState] = useState(null); // checking | paid | expired | error
  const { setUser } = useAuth();
  const attempts = useRef(0);
  const pollStarted = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const pollStatus = useCallback(
    async (sessionId) => {
      if (!mounted.current) return;
      if (attempts.current >= MAX_ATTEMPTS) {
        setPaymentState("error");
        return;
      }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!mounted.current) return;
        if (data.payment_status === "paid") {
          setPaymentState("paid");
          const { data: me } = await api.get("/auth/me");
          setUser(me);
          toast.success("Welcome to Evervow Premium!");
          return;
        }
        if (data.status === "expired") {
          setPaymentState("expired");
          return;
        }
        setTimeout(() => pollStatus(sessionId), 2000);
      } catch (error) {
        logError("Payment status check failed:", error);
        setPaymentState("error");
      }
    },
    [setUser, setPaymentState]
  );

  useEffect(() => {
    if (pollStarted.current) return;
    const sessionId = params.get("session_id");
    if (sessionId) {
      pollStarted.current = true;
      setPaymentState("checking");
      pollStatus(sessionId);
      setParams({}, { replace: true });
    }
  }, [params, setParams, pollStatus, setPaymentState]);

  return paymentState;
}
